import React, { ReactNode, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import NavBar from '../NavBar';
import Svg from '../Svg';
import Icon from '../Icon';
import IconButton from '../IconButton';
import Dropdown from '../Dropdown';
import DropdownFilter from '../Dropdown/DropdownFilter';
import './Header.css';

function Header({
  children,
  menuOptions,
  isReturnEnabled,
  routeName = '',
  onClickReturnButton,
  isSticky,
  WhiteLabeling,
  workspacesOptions = [],
  ...props
}): ReactNode {
  const { t } = useTranslation('Header');
  const location = useLocation();

  const searchEntries = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return Object.fromEntries(searchParams.entries());
  }, [location.search]);

  const currentWorkspace = useMemo(() => {
    if (!searchEntries.workspace || !workspacesOptions?.length) {
      return null;
    }
    return workspacesOptions.find(workspace => workspace.id === searchEntries.workspace);
  }, [searchEntries.workspace, workspacesOptions]);

  const onClickReturn = useCallback(() => {
    if (isReturnEnabled && onClickReturnButton) {
      onClickReturnButton();
    }
  }, [isReturnEnabled, onClickReturnButton]);

  const containerClasses = useMemo(() => {
    return classNames(
      'flex flex-1 justify-between px-2',
      {
        'mx-1': routeName === 'viewer',
        'max-w-[1420px] mx-auto': routeName !== 'viewer',
      }
    );
  }, [routeName]);

  const logoClasses = useMemo(() => {
    return classNames(
      'inline-flex items-center',
      { 'cursor-pointer': isReturnEnabled }
    );
  }, [isReturnEnabled]);

  const logoComponent = useMemo(() => {
    return WhiteLabeling?.createLogoComponentFn?.(React, props) || <Svg name="logo-harpiAI" />;
  }, [WhiteLabeling, props]);

  const shouldShowWorkspaces = useMemo(() => {
    return workspacesOptions?.length > 0;
  }, [workspacesOptions?.length]);

  const WorkspaceSelector = useMemo(() => {
    if (!shouldShowWorkspaces) return null;

    return (
      <>
        {currentWorkspace && (
          <span className="text-sm text-primary-active mr-3 mt-1">
            {currentWorkspace.title}
          </span>
        )}

        <DropdownFilter
          id="workspaces"
          showDropdownIcon={false}
          list={workspacesOptions}
          alignment="right"
        >
          <IconButton
            id="workspaces-icon"
            variant="text"
            color="inherit"
            size="initial"
            className="text-primary-active"
            aria-label={t('Select workspace')}
          >
            <Icon name="building" />
          </IconButton>

          <IconButton
            id="workspaces-chevron-down-icon"
            variant="text"
            color="inherit"
            size="initial"
            className="text-primary-active mr-5"
            aria-label={t('Expand workspace menu')}
          >
            <Icon name="chevron-down" />
          </IconButton>
        </DropdownFilter>
      </>
    );
  }, [shouldShowWorkspaces, currentWorkspace, workspacesOptions, t]);

  return (
    <NavBar
      className="justify-between border-none z-[1000]"
      isSticky={isSticky}
    >
      <div className={containerClasses}>
        <div className="flex items-center">
          <div
            className={logoClasses}
            onClick={onClickReturn}
            data-cy="return-to-work-list"
            role={isReturnEnabled ? 'button' : undefined}
            tabIndex={isReturnEnabled ? 0 : undefined}
            onKeyDown={isReturnEnabled ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClickReturn();
              }
            } : undefined}
            aria-label={isReturnEnabled ? t('Return to worklist') : undefined}
          >
            {isReturnEnabled && (
              <div className="header-return-btn flex items-center justify-center w-8 h-8 rounded-lg mr-1">
                <Icon name="chevron-left" className="w-4 h-4" />
              </div>
            )}
            <div>{logoComponent}</div>
          </div>
        </div>

        <div className="flex items-center flex-1 justify-center px-4">
          {children}
        </div>

        <div className="flex items-center gap-2">
          {shouldShowWorkspaces && (
            <DropdownFilter
              id="workspaces"
              showDropdownIcon={false}
              list={workspacesOptions}
              alignment="right"
              itemsClassName="text-indigo-300"
            >
              <div
                className="header-workspace-btn flex items-center gap-2 px-3 h-8 rounded-lg cursor-pointer"
                aria-label={t('Select workspace')}
              >
                <Icon name="building" className="w-4 h-4 flex-shrink-0 fill-white" />
                {currentWorkspace && (
                  <span className="header-workspace-title text-xs font-semibold max-w-[120px] truncate">
                    {currentWorkspace.title}
                  </span>
                )}
                <Icon name="chevron-down" className="w-3 h-3 flex-shrink-0 opacity-60" />
              </div>
            </DropdownFilter>
          )}

          <Dropdown
            id="options"
            showDropdownIcon={false}
            list={menuOptions}
            alignment="right"
            itemsClassName="text-indigo-300"
          >
            <div
              className="header-settings-btn flex items-center justify-center w-8 h-8 rounded-lg cursor-pointer"
              aria-label={t('Settings')}
            >
              <Icon name="settings" className="w-4 h-4" />
            </div>
          </Dropdown>
        </div>
      </div>
    </NavBar>
  );
}

Header.propTypes = {
  menuOptions: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      icon: PropTypes.string,
      onClick: PropTypes.func.isRequired,
    })
  ),
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  isReturnEnabled: PropTypes.bool,
  isSticky: PropTypes.bool,
  onClickReturnButton: PropTypes.func,
  WhiteLabeling: PropTypes.object,
  workspacesOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      icon: PropTypes.string,
      onClick: PropTypes.func,
    })
  ),
  routeName: PropTypes.string,
};

Header.defaultProps = {
  isReturnEnabled: true,
  isSticky: false,
  routeName: '',
  workspacesOptions: [],
};

export default Header;
